import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  FieldMapperHelper,
} from '@zendra/shared';
import { PlanTypeService } from 'cbd-proxies';
import { finalize, map } from 'rxjs';
import { IPlanCreationOptions } from './interfaces/IPlanCreationOptions';

@Injectable()
export class PlanCreationService {
  private readonly planTypeService = inject(PlanTypeService);
  private readonly toaster = inject(ToasterService);
  private lookupInProgress = false;

  openCreate(options: IPlanCreationOptions): void {
    if (this.lookupInProgress || options.destroyRef.destroyed) return;
    this.lookupInProgress = true;

    this.planTypeService.getList({
      name: options.planType,
      skipCount: 0,
      maxResultCount: DEFAULT_MAX_RESULT_COUNT,
    }).pipe(
      map(result => {
        const matches = (result.items ?? []).filter(item => item.name === options.planType);
        if (matches.length !== 1) {
          throw new Error('Operational::Errors.LookupPlanType');
        }
        return { planTypeId: matches[0].id, planTypeName: options.planType };
      }),
      takeUntilDestroyed(options.destroyRef),
      finalize(() => {
        this.lookupInProgress = false;
      }),
    ).subscribe({
      next: values => {
        const fields = FieldMapperHelper.mapFieldsForForm(
          options.fields,
          values,
          options.injector,
        ).map(field => field.key === 'planTypeId'
          ? {
              ...field,
              options: [{ key: String(values.planTypeId), value: values.planTypeName }],
              optionsLoader: undefined,
            }
          : field);

        options.onResolved(values);
        options.form.open({
          title: options.title,
          fields,
          entityLocaleKey: options.entityLocaleKey,
          mode: 'create',
        });
      },
      error: () => {
        this.toaster.error('Operational::Errors.LookupPlanType', '', { life: DEFAULT_TOASTER_LIFE });
      },
    });
  }
}
